import Vue from 'vue';
import { TM_RECONCILIATION_API, ERROR_MESSAGES } from '../utils/constants';

// Use Vue.axios which is set up by VueAxios plugin in main.js
const $axios = Vue.axios;

export default {
  namespaced: true,
  
  state: {
    // Summary combo dropdown items
    summaryCombo: [],
    // Reconciliation data to display in table
    reconciliationSummaryData: [],
    // Reconciliation collection name
    reconciliationCollection: '',
    // Loading state for API calls
    loading: false,
    // Error state
    error: null
  },

  mutations: {
    SET_SUMMARY_COMBO(state, data) {
      state.summaryCombo = data;
    },
    
    SET_RECONCILIATION_SUMMARY_DATA(state, data) {
      state.reconciliationSummaryData = data;
    },
    
    SET_RECONCILIATION_COLLECTION(state, collection) {
      state.reconciliationCollection = collection;
    },
    
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    
    SET_ERROR(state, error) {
      state.error = error;
    },
    
    CLEAR_ERROR(state) {
      state.error = null;
    },

    CLEAR_SUMMARY_DATA(state) {
      state.reconciliationSummaryData = [];
    }
  },

  actions: {
    /**
     * Fetch overall reconciliation summary
     * @param {Object} context - Vuex context
     * @param {Object} payload - { collection }
     */
    async fetchOverallReconSummary({ commit }, { collection }) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      commit('SET_RECONCILIATION_COLLECTION', collection);
      
      try {
        const data = { collection };
        const response = await $axios.post(TM_RECONCILIATION_API.GET_OVERALL_SUMMARY, { data });
        
        if (response.data.status === "success") {
          commit('SET_RECONCILIATION_SUMMARY_DATA', response.data.data);
        } else {
          const errorMsg = response.data.message || ERROR_MESSAGES.ERROR_GET_COMBO_RESULT;
          commit('SET_ERROR', errorMsg);
          commit('sbError', errorMsg, { root: true });
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        const errorMsg = err.message || err || ERROR_MESSAGES.UNKNOWN_ERROR;
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        console.error(err);
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Fetch combo-specific reconciliation summary
     * @param {Object} context - Vuex context
     * @param {Object} payload - { collection, combo }
     */
    async fetchComboReconSummary({ commit }, { collection, combo }) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      commit('SET_RECONCILIATION_COLLECTION', collection);
      
      try {
        const data = { collection };
        const response = await $axios.post(
          `${TM_RECONCILIATION_API.GET_COMBO_SUMMARY}${combo}`,
          { data }
        );
        
        if (response.data.status === "success") {
          commit('SET_RECONCILIATION_SUMMARY_DATA', response.data.data);
        } else {
          const errorMsg = response.data.message || ERROR_MESSAGES.ERROR_GET_COMBO_RESULT;
          commit('SET_ERROR', errorMsg);
          commit('sbError', errorMsg, { root: true });
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        const errorMsg = err.message || err || ERROR_MESSAGES.UNKNOWN_ERROR;
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        console.error(err);
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Fetch unique combo codes for annexure list
     * @param {Object} context - Vuex context
     * @param {Object} payload - { collection, type }
     */
    async fetchUniqueComboCodes({ commit }, { collection, type }) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      commit('SET_RECONCILIATION_COLLECTION', collection);
      
      try {
        const response = await $axios.post(
          `${TM_RECONCILIATION_API.GET_UNIQUE_COMBO_CODES}${collection}`
        );
        
        if (response.data.status === "success") {
          let summaryObj = {};
          
          if (type === 'Result') {
            summaryObj = { 'combo-code': 'Summary' };
          } else {
            summaryObj = { 'combo-code': 'Reconciliation ' };
          }
          
          const summaryCombo = response.data.data || [];
          summaryCombo.unshift(summaryObj);
          commit('SET_SUMMARY_COMBO', summaryCombo);
        } else {
          const errorMsg = response.data.message || ERROR_MESSAGES.ERROR_GET_COMBO_CODE;
          commit('SET_ERROR', errorMsg);
          commit('sbError', errorMsg, { root: true });
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        const errorMsg = err.message || err || ERROR_MESSAGES.UNKNOWN_ERROR;
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        console.error(err);
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    }
  },

  getters: {
    summaryCombo: state => state.summaryCombo,
    reconciliationSummaryData: state => state.reconciliationSummaryData,
    reconciliationCollection: state => state.reconciliationCollection,
    loading: state => state.loading,
    error: state => state.error
  }
};
